import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { playerPosition } from "./controls";
import { playRabbitSound } from "./audio";

interface CritterProps {
  center: [number, number, number];
  radius: number;
  speed: number;
  phase: number;
  color?: string;
}

const SOUND_RADIUS = 6;

/** A small decorative four-legged critter (rabbit-like) that hops around a home patch. */
const Critter: React.FC<CritterProps> = ({ center, radius, speed, phase, color = "#b8895a" }) => {
  const groupRef = useRef<THREE.Group>(null);
  const soundTimer = useRef(1 + Math.random() * 3);

  useFrame(({ clock }, delta) => {
    const t = clock.getElapsedTime() * speed + phase;
    const x = center[0] + Math.sin(t) * radius;
    const z = center[2] + Math.cos(t * 0.7) * radius * 0.7;
    const hop = Math.abs(Math.sin(clock.getElapsedTime() * 6 + phase)) * 0.12;
    if (groupRef.current) {
      groupRef.current.position.set(x, center[1] + hop, z);
      const dx = Math.cos(t) * radius;
      const dz = -Math.sin(t * 0.7) * radius * 0.7 * 0.7;
      groupRef.current.rotation.y = Math.atan2(-dx, -dz);
    }

    soundTimer.current -= delta;
    if (soundTimer.current <= 0) {
      soundTimer.current = 4 + Math.random() * 6;
      if (groupRef.current && playerPosition.distanceTo(groupRef.current.position) < SOUND_RADIUS) {
        playRabbitSound();
      }
    }
  });

  return (
    <group ref={groupRef} scale={0.42}>
      {/* Body */}
      <mesh position={[0, 0.28, 0]} castShadow>
        <boxGeometry args={[0.34, 0.28, 0.5]} />
        <meshStandardMaterial color={color} roughness={0.9} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.4, -0.32]} castShadow>
        <boxGeometry args={[0.24, 0.24, 0.24]} />
        <meshStandardMaterial color={color} roughness={0.9} />
      </mesh>
      {/* Ears */}
      <mesh position={[-0.07, 0.62, -0.32]} castShadow>
        <boxGeometry args={[0.06, 0.24, 0.08]} />
        <meshStandardMaterial color={color} roughness={0.9} />
      </mesh>
      <mesh position={[0.07, 0.62, -0.32]} castShadow>
        <boxGeometry args={[0.06, 0.24, 0.08]} />
        <meshStandardMaterial color={color} roughness={0.9} />
      </mesh>
      {/* Legs */}
      {[
        [-0.12, 0.08, 0.16],
        [0.12, 0.08, 0.16],
        [-0.12, 0.08, -0.16],
        [0.12, 0.08, -0.16],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.08, 0.16, 0.08]} />
          <meshStandardMaterial color={color} roughness={0.9} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.34, 0.28]}>
        <boxGeometry args={[0.1, 0.1, 0.1]} />
        <meshStandardMaterial color="#f0e6d8" roughness={0.9} />
      </mesh>
    </group>
  );
};

export default Critter;
